import React, { useState, useEffect, useMemo } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, Button } from '../../components/ui'
import { Select } from '../../components/ui/Select'
import { API_ENDPOINTS } from '../../config/api'
import { Calendar, Filter, X, FileText, Shield, Users, AlertTriangle, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react'
import { PageLayout } from '../../layouts/PageLayout'

interface GovernanceEvent {
  id: string
  title: string
  type: string
  date: string
  status: string
  relatedPolicyId?: string
  relatedAuditId?: string
  relatedRiskId?: string
  relatedTrainingId?: string
}

const GovernanceCalendar: React.FC = () => {
  const { t } = useI18n()
  const { token } = useAuth()
  const navigate = useNavigate()
  const [events, setEvents] = useState<GovernanceEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')

  useEffect(() => {
    loadEvents()
  }, [currentDate, typeFilter, statusFilter])

  const loadEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get first and last day of current month
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth()
      const from = new Date(year, month, 1)
      const to = new Date(year, month + 1, 0, 23, 59, 59)

      const params = new URLSearchParams()
      params.append('from', from.toISOString())
      params.append('to', to.toISOString())
      if (typeFilter) params.append('type', typeFilter)
      if (statusFilter) params.append('status', statusFilter)

      const url = `${API_ENDPOINTS.GOVERNANCE_CALENDAR_EVENTS}?${params.toString()}`
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) throw new Error('Failed to load events')
      const data = await res.json()
      setEvents(data.events || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load events')
      console.error('[GovernanceCalendar] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleEventClick = (event: GovernanceEvent) => {
    if (event.relatedPolicyId) {
      navigate(`/governance/policies`)
    } else if (event.relatedAuditId) {
      navigate(`/governance/audits`)
    } else if (event.relatedRiskId) {
      navigate(`/governance/risks`)
    } else if (event.relatedTrainingId) {
      navigate(`/governance/training`)
    }
  }

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'policy_review':
        return <FileText className="w-4 h-4" />
      case 'internal_audit':
      case 'external_audit':
        return <Shield className="w-4 h-4" />
      case 'risk_review':
        return <AlertTriangle className="w-4 h-4" />
      case 'training':
        return <Users className="w-4 h-4" />
      default:
        return <Calendar className="w-4 h-4" />
    }
  }

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'policy_review':
        return 'bg-blue-100 text-blue-700 border-blue-300'
      case 'internal_audit':
        return 'bg-green-100 text-green-700 border-green-300'
      case 'external_audit':
        return 'bg-purple-100 text-purple-700 border-purple-300'
      case 'risk_review':
        return 'bg-red-100 text-red-700 border-red-300'
      case 'training':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'policy_review':
        return 'Policy Review'
      case 'internal_audit':
        return 'Internal Audit'
      case 'external_audit':
        return 'External Audit'
      case 'risk_review':
        return 'Risk Review'
      case 'training':
        return 'Training'
      default:
        return type
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'text-green-600'
      case 'Missed':
        return 'text-red-600'
      default:
        return 'text-blue-600'
    }
  }

  // Group events by date
  const eventsByDate = useMemo(() => {
    const grouped: Record<string, GovernanceEvent[]> = {}
    events.forEach(event => {
      const dateKey = new Date(event.date).toISOString().split('T')[0]
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(event)
    })
    return grouped
  }, [events])

  // Calendar grid
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - startDate.getDay()) // Start from Sunday
    
    const days: Array<{ date: Date; isCurrentMonth: boolean; events: GovernanceEvent[] }> = []
    const current = new Date(startDate)
    
    while (days.length < 42) { // 6 weeks
      const dateKey = current.toISOString().split('T')[0]
      days.push({
        date: new Date(current),
        isCurrentMonth: current.getMonth() === month,
        events: eventsByDate[dateKey] || []
      })
      current.setDate(current.getDate() + 1)
    }
    
    return days
  }, [currentDate, eventsByDate])

  // Upcoming events (next 30 days)
  const upcomingEvents = useMemo(() => {
    const now = new Date()
    const future = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    return events
      .filter(e => {
        const eventDate = new Date(e.date)
        return eventDate >= now && eventDate <= future && e.status === 'Planned'
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 10)
  }, [events])

  const uniqueTypes = useMemo(() => {
    return Array.from(new Set(events.map(e => e.type))).sort()
  }, [events])

  const typeFilterOptions = useMemo(
    () => [
      { value: '', label: 'All Types' },
      ...uniqueTypes.map((type) => ({ value: type, label: getEventTypeLabel(type) })),
    ],
    [uniqueTypes],
  )

  const statusFilterOptions = useMemo(
    () => [
      { value: '', label: 'All Statuses' },
      { value: 'Planned', label: 'Planned' },
      { value: 'Completed', label: 'Completed' },
      { value: 'Missed', label: 'Missed' },
    ],
    [],
  )

  const navigateMonth = (direction: number) => {
    const newDate = new Date(currentDate)
    newDate.setMonth(newDate.getMonth() + direction)
    setCurrentDate(newDate)
  }

  if (loading && events.length === 0) {
    return (
      <PageLayout pageScope="governance-calendar-page" title={t('governance.calendar.title', 'Governance Calendar')}>
        <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
      </PageLayout>
    )
  }

  return (
    <PageLayout pageScope="governance-calendar-page" title={t('governance.calendar.title', 'Governance Calendar')}
      actions={
        <Button
          type="button"
          size="small"
          color="outline"
          onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')}
          className="px-4 py-2"
        >
          {viewMode === 'calendar' ? 'List View' : 'Calendar View'}
        </Button>
      }
    >
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Filters</h3>
            <div className="flex items-center gap-2">
              {(typeFilter || statusFilter) && (
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  icon={<X className="w-4 h-4" />}
                  onClick={() => {
                    setTypeFilter('')
                    setStatusFilter('')
                  }}
                  className="border-0 bg-transparent text-sm text-gray-600 shadow-none hover:bg-gray-100 hover:text-gray-900"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <Select<string>
                className="w-full text-sm"
                minWidth="100%"
                options={typeFilterOptions}
                value={typeFilter}
                onChange={(v) => setTypeFilter(v ?? '')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <Select<string>
                className="w-full text-sm"
                minWidth="100%"
                options={statusFilterOptions}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v ?? '')}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {viewMode === 'calendar' ? (
        <>
          {/* Calendar Header */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  iconOnly
                  icon={<ChevronLeft className="w-5 h-5" />}
                  onClick={() => navigateMonth(-1)}
                  className="p-2 hover:bg-gray-100 rounded"
                />
                <h2 className="text-xl font-semibold">
                  {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h2>
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  iconOnly
                  icon={<ChevronRight className="w-5 h-5" />}
                  onClick={() => navigateMonth(1)}
                  className="p-2 hover:bg-gray-100 rounded"
                />
              </div>
            </CardHeader>
            <CardContent>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Day headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-gray-600">
                    {day}
                  </div>
                ))}
                {/* Calendar days */}
                {calendarDays.map((day, idx) => (
                  <div
                    key={idx}
                    className={`min-h-[100px] p-2 border rounded ${
                      day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                    } ${selectedDate && day.date.toDateString() === selectedDate.toDateString() ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => setSelectedDate(day.date)}
                  >
                    <div className={`text-sm font-medium mb-1 ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                      {day.date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {day.events.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          className={`text-xs p-1 rounded border cursor-pointer hover:opacity-80 ${getEventTypeColor(event.type)}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEventClick(event)
                          }}
                          title={event.title}
                        >
                          <div className="flex items-center gap-1">
                            {getEventTypeIcon(event.type)}
                            <span className="truncate">{event.title}</span>
                          </div>
                        </div>
                      ))}
                      {day.events.length > 3 && (
                        <div className="text-xs text-gray-500">
                          +{day.events.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="mb-6">
          <CardHeader>
            <h3 className="text-lg font-semibold">All Events</h3>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No events found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {events.map(event => (
                  <div
                    key={event.id}
                    className="flex items-start justify-between p-4 border rounded hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded ${getEventTypeColor(event.type)}`}>
                        {getEventTypeIcon(event.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{event.title}</h4>
                          <span className={`px-2 py-1 rounded text-xs ${getEventTypeColor(event.type)}`}>
                            {getEventTypeLabel(event.type)}
                          </span>
                          <span className={`text-xs font-medium ${getStatusColor(event.status)}`}>
                            {event.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {new Date(event.date).toLocaleString('default', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Events */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">{t('governance.calendar.upcoming', 'Upcoming Events')} (Next 30 Days)</h3>
        </CardHeader>
        <CardContent>
          {upcomingEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No upcoming events</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingEvents.map(event => (
                <div
                  key={event.id}
                  className="flex items-start justify-between p-3 border rounded hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleEventClick(event)}
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded ${getEventTypeColor(event.type)}`}>
                      {getEventTypeIcon(event.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{event.title}</h4>
                        <span className={`px-2 py-1 rounded text-xs ${getEventTypeColor(event.type)}`}>
                          {getEventTypeLabel(event.type)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(event.date).toLocaleDateString('default', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  )
}

export default GovernanceCalendar
